<template>
  <div class="column content-box">
    <woot-modal-header
      :header-title="$t('LABEL_MGMT.ADD.TITLE')"
      :header-content="$t(`${i18nPrefix}.ADD.DESC`)"
    />
    <form class="row" @submit.prevent="addLabel">
      <div v-if="availableTypes.length > 1" class="medium-12 columns">
        <label>
          {{ $t('LABEL_MGMT.ADD.TYPE.LABEL') }}
          <div class="type-selector">
            <div
              v-for="type in availableTypes"
              :key="type.key"
              class="type-card"
              :class="{ active: type.key === selectedTypeKey }"
              :data-testid="`label-type-${type.key}`"
              @click="selectedTypeKey = type.key"
            >
              <fluent-icon
                :icon="type.icon"
                size="28"
                class="type-card--icon"
              />
              <span>{{ $t(`${type.i18nPrefix}.TYPE_NAME`) }}</span>
            </div>
          </div>
        </label>
      </div>

      <woot-input
        v-model.trim="title"
        :class="{ error: $v.title.$error }"
        class="medium-12 columns label-name--input"
        :label="$t(`${i18nPrefix}.FORM.NAME.LABEL`)"
        :placeholder="$t(`${i18nPrefix}.FORM.NAME.PLACEHOLDER`)"
        :error="getLabelTitleErrorMessage"
        data-testid="label-title"
        @input="$v.title.$touch"
      />

      <woot-input
        v-model.trim="description"
        :class="{ error: $v.description.$error }"
        class="medium-12 columns"
        :label="$t(`${i18nPrefix}.FORM.DESCRIPTION.LABEL`)"
        :placeholder="$t(`${i18nPrefix}.FORM.DESCRIPTION.PLACEHOLDER`)"
        data-testid="label-description"
        @input="$v.description.$touch"
      />

      <div class="medium-12">
        <label>
          {{ $t(`${i18nPrefix}.FORM.COLOR.LABEL`) }}
          <woot-color-picker v-model="color" />
        </label>
      </div>
      <div class="medium-12">
        <input v-model="showOnSidebar" type="checkbox" :value="true" />
        <label for="conversation_creation">
          {{ $t(`${i18nPrefix}.FORM.SHOW_ON_SIDEBAR.LABEL`) }}
        </label>
      </div>
      <div class="modal-footer">
        <div class="medium-12 columns">
          <woot-button
            :is-disabled="$v.title.$invalid || uiFlags.isCreating"
            :is-loading="uiFlags.isCreating"
            data-testid="label-submit"
          >
            {{ $t(`${i18nPrefix}.FORM.CREATE`) }}
          </woot-button>
          <woot-button class="button clear" @click.prevent="onClose">
            {{ $t(`${i18nPrefix}.FORM.CANCEL`) }}
          </woot-button>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import alertMixin from 'shared/mixins/alertMixin';
import validationMixin from './validationMixin';
import { mapGetters } from 'vuex';
import validations from './validations';
import { getRandomColor } from 'dashboard/helper/labelColor';
import {
  DEFAULT_LABEL_TYPE,
  findLabelType,
  getAvailableLabelTypes,
} from './labelTypes';

export default {
  mixins: [alertMixin, validationMixin],
  props: {
    initialType: {
      type: String,
      default: DEFAULT_LABEL_TYPE,
    },
  },
  data() {
    return {
      color: '#000',
      description: '',
      title: '',
      showOnSidebar: true,
      selectedTypeKey: this.initialType,
    };
  },
  validations,
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
    }),
    availableTypes() {
      return getAvailableLabelTypes(flag =>
        this.isFeatureEnabledonAccount(this.accountId, flag)
      );
    },
    selectedType() {
      return findLabelType(this.selectedTypeKey);
    },
    i18nPrefix() {
      return this.selectedType.i18nPrefix;
    },
    uiFlags() {
      return this.$store.getters[`${this.selectedType.storeModule}/getUIFlags`];
    },
  },
  mounted() {
    this.color = getRandomColor();
    const isAvailable = this.availableTypes.some(
      type => type.key === this.selectedTypeKey
    );
    if (!isAvailable) {
      this.selectedTypeKey = DEFAULT_LABEL_TYPE;
    }
  },
  methods: {
    onClose() {
      this.$emit('close');
    },
    async addLabel() {
      const { storeModule, i18nPrefix } = this.selectedType;
      try {
        await this.$store.dispatch(`${storeModule}/create`, {
          color: this.color,
          description: this.description,
          title: this.title.toLowerCase(),
          show_on_sidebar: this.showOnSidebar,
        });
        this.showAlert(this.$t(`${i18nPrefix}.ADD.API.SUCCESS_MESSAGE`));
        this.onClose();
      } catch (error) {
        const errorMessage =
          error.message || this.$t(`${i18nPrefix}.ADD.API.ERROR_MESSAGE`);
        this.showAlert(errorMessage);
      }
    },
  },
};
</script>
<style lang="scss" scoped>
// Label API supports only lowercase letters
.label-name--input {
  ::v-deep {
    input {
      text-transform: lowercase;
    }
  }
}

.type-selector {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-normal);
  margin-top: var(--space-slab);
  margin-bottom: var(--space-normal);
}

.type-card {
  width: 14rem;
  height: 14rem;
  border-radius: var(--border-radius-large);
  background: var(--s-50);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid transparent;
  color: var(--s-500);

  span {
    font-weight: var(--font-weight-medium);
  }

  &.active {
    background: var(--w-700);
    border-color: var(--w-700);
    color: var(--white);
  }
}

.type-card--icon {
  margin-bottom: var(--space-small);
}

.modal-footer {
  width: 100%;

  div {
    display: flex;
    justify-content: flex-end;
  }
}
</style>
